import { useState } from "react";
import { BedDouble, Loader2, Plus, Search, Check } from "lucide-react";
import { adminQuoteRooms, eur } from "../../../lib/quotesApi";

const input = "rounded-lg border border-[#e8dcc8] bg-white px-2.5 py-2 text-sm text-[#2b1410]";
const uid = () => Math.random().toString(36).slice(2, 10);
const fmt = (d) => { try { return new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "short" }); } catch { return d; } };

export function RoomPicker({ dateFrom, dateTo, guests, items, onAdd }) {
  const [checkin, setCheckin] = useState(dateFrom || "");
  const [checkout, setCheckout] = useState(dateTo || "");
  const [adults, setAdults] = useState(guests || 2);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const added = new Set(items.map((it) => it.catalog_id));

  const search = async () => {
    if (!checkin || !checkout) { setErr("Scegli arrivo e partenza"); return; }
    setBusy(true); setErr(""); setRes(null);
    try { setRes(await adminQuoteRooms(checkin, checkout, adults)); }
    catch (e) { setErr(e?.response?.data?.detail || "FullBoard non raggiungibile"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-[#e8dcc8] overflow-hidden" data-testid="room-picker">
      <div className="px-4 py-2.5 border-b border-[#e8dcc8] flex flex-wrap items-end gap-2">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#C48B31] font-bold mr-auto inline-flex items-center gap-1.5 self-center"><BedDouble className="h-3.5 w-3.5" /> Camere · prezzi e disponibilità live da FullBoard</div>
        <label className="text-[11px] text-[#7c5a4e]">Arrivo<input type="date" value={checkin} onChange={(e) => setCheckin(e.target.value)} className={`${input} block mt-0.5`} data-testid="room-picker-checkin" /></label>
        <label className="text-[11px] text-[#7c5a4e]">Partenza<input type="date" min={checkin} value={checkout} onChange={(e) => setCheckout(e.target.value)} className={`${input} block mt-0.5`} data-testid="room-picker-checkout" /></label>
        <label className="text-[11px] text-[#7c5a4e]">Adulti<input type="number" min="1" max="6" value={adults} onChange={(e) => setAdults(Number(e.target.value) || 1)} className={`${input} block mt-0.5 w-16`} data-testid="room-picker-adults" /></label>
        <button onClick={search} disabled={busy} className="h-9 px-4 rounded-full bg-[#4a1c17] text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="room-picker-search">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Cerca camere</button>
      </div>
      {err && <div className="px-4 py-3 text-sm text-red-700" data-testid="room-picker-error">{err}</div>}
      {res && res.rooms.length === 0 && <div className="px-4 py-4 text-sm text-[#7c5a4e]" data-testid="room-picker-empty">Nessuna camera libera dal {fmt(res.checkin)} al {fmt(res.checkout)} per {res.adults} adulti.</div>}
      {res && res.rooms.length > 0 && (
        <ul className="divide-y divide-[#f0e6d8]" data-testid="room-picker-list">
          {res.rooms.map((r) => {
            const isAdded = added.has(r.item.catalog_id);
            return (
              <li key={r.name} className="p-3 flex items-center gap-3" data-testid={`room-picker-row-${r.name.toLowerCase()}`}>
                {r.image ? <img src={r.image} alt="" className="h-12 w-16 rounded-lg object-cover shrink-0" /> : <div className="h-12 w-16 rounded-lg bg-[#faf3ee] shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[#2b1410]">Camera {r.name} <span className="text-xs font-normal text-[#7c5a4e]">· {r.seasons.join(" · ") || "tariffa online"}</span></div>
                  <div className="text-xs text-[#7c5a4e]">{eur(r.item.unit_cents)}/notte · {r.nights} {r.nights === 1 ? "notte" : "notti"} · totale <span className="font-semibold text-[#4a1c17]">{eur(r.total_cents)}</span></div>
                </div>
                <button onClick={() => onAdd({ id: uid(), ...r.item })} disabled={isAdded} className={`h-9 px-3 rounded-full text-sm font-semibold inline-flex items-center gap-1 ${isAdded ? "bg-emerald-50 text-emerald-800" : "border border-[#4a1c17] text-[#4a1c17] hover:bg-[#faf3ee]"}`} data-testid={`room-picker-add-${r.name.toLowerCase()}`}>
                  {isAdded ? <><Check className="h-4 w-4" /> Aggiunta</> : <><Plus className="h-4 w-4" /> Aggiungi</>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
